import { PropertyStatus } from 'src/ts/enums';
import { DEFAULT_PROPERTY_STATUSES } from './buildPropertyFilter';

export const DEFAULT_ROWS_PER_PAGE = 20;
export const ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 50, 100, 250, 500];

export type PropertyListUrlState = {
  companyIds: string;
  statuses: PropertyStatus[];
  q: string;
  page: number;
  rowsPerPage: number;
};

// Number(null) and Number('') are both 0, so a missing param has to be rejected
// before the numeric check, or every default silently becomes zero.
const toCount = (raw: string | null, fallback: number, min = 0): number => {
  if (raw === null || raw.trim() === '') return fallback;

  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed >= min ? parsed : fallback;
};

const parseRowsPerPage = (raw: string | null): number => {
  const parsed = toCount(raw, DEFAULT_ROWS_PER_PAGE);
  return ROWS_PER_PAGE_OPTIONS.includes(parsed) ? parsed : DEFAULT_ROWS_PER_PAGE;
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const parseCompanyIds = (raw: string | null): string =>
  (raw || '')
    .split(',')
    .filter((id) => UUID.test(id))
    .join(',');

const parseStatuses = (raw: string | null): PropertyStatus[] => {
  if (!raw) return [];
  const wanted = raw.split(',');
  return DEFAULT_PROPERTY_STATUSES.filter((status) => wanted.includes(status));
};

/**
 * Without `allowFilters`, companies and statuses are dropped so the URL never
 * shows a filter the page is not applying.
 */
export const readPropertyListParams = (
  params: URLSearchParams,
  { allowFilters }: { allowFilters: boolean }
): PropertyListUrlState => ({
  companyIds: allowFilters ? parseCompanyIds(params.get('companies')) : '',
  statuses: allowFilters ? parseStatuses(params.get('statuses')) : [],
  q: params.get('q') || '',
  page: toCount(params.get('page'), 0),
  rowsPerPage: parseRowsPerPage(params.get('rowsPerPage'))
});

export const writePropertyListParams = (state: PropertyListUrlState): Record<string, string> => {
  const params: Record<string, string> = {};

  if (state.companyIds) params.companies = state.companyIds;
  if (state.statuses.length > 0) params.statuses = state.statuses.join(',');
  if (state.q) params.q = state.q;
  if (state.page > 0) params.page = String(state.page);
  if (state.rowsPerPage !== DEFAULT_ROWS_PER_PAGE) params.rowsPerPage = String(state.rowsPerPage);

  return params;
};
